import { Store } from 'Application/Store/Store';
import {ihm_viewer__dictionary, locale} from "../../../Application/i18n/i18nViewer";

/**
 * Systeme d'initialisation avec phases explicites
 * Orchestre tout le flow de chargement de l'application
 * 
 * Flow des phases :
 *   STORE_READY -> PROJECT_FETCHING -> PROJECT_DOWNLOADING -> PROJECT_STORING 
 *   -> SCENE_CREATED -> MODELS_LOADED -> ANIMATION_READY 
 *   -> COMPARATIVE_READY -> UI_READY -> COMPLETE
 */

export const INIT_PHASES = {
  STORE_READY: 'STORE_READY',
  PROJECT_FETCHING: 'PROJECT_FETCHING',
  PROJECT_DOWNLOADING: 'PROJECT_DOWNLOADING',
  PROJECT_STORING: 'PROJECT_STORING',
  SCENE_CREATED: 'SCENE_CREATED',
  MODELS_LOADED: 'MODELS_LOADED',
  ANIMATION_READY: 'ANIMATION_READY',
  COMPARATIVE_READY: 'COMPARATIVE_READY',
  UI_READY: 'UI_READY',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR'
};

/**
 * Labels affichables pour chaque phase
 */
const getPhaseLabels = () => ({
  [INIT_PHASES.STORE_READY]: ihm_viewer__dictionary.INIT_PHASES.STORE_READY[locale()],
  [INIT_PHASES.PROJECT_FETCHING]: ihm_viewer__dictionary.INIT_PHASES.PROJECT_FETCHING[locale()],
  [INIT_PHASES.PROJECT_DOWNLOADING]: ihm_viewer__dictionary.INIT_PHASES.PROJECT_DOWNLOADING[locale()],
  [INIT_PHASES.PROJECT_STORING]: ihm_viewer__dictionary.INIT_PHASES.PROJECT_STORING[locale()],
  [INIT_PHASES.SCENE_CREATED]: ihm_viewer__dictionary.INIT_PHASES.SCENE_CREATED[locale()],
  [INIT_PHASES.MODELS_LOADED]: ihm_viewer__dictionary.INIT_PHASES.MODELS_LOADED[locale()],
  [INIT_PHASES.ANIMATION_READY]: ihm_viewer__dictionary.INIT_PHASES.ANIMATION_READY[locale()],
  [INIT_PHASES.COMPARATIVE_READY]: ihm_viewer__dictionary.INIT_PHASES.COMPARATIVE_READY[locale()],
  [INIT_PHASES.UI_READY]: ihm_viewer__dictionary.INIT_PHASES.UI_READY[locale()],
  [INIT_PHASES.COMPLETE]: ihm_viewer__dictionary.INIT_PHASES.COMPLETE[locale()]
});

/**
 * Ordre des phases pour calculer la progression
 */
const PHASE_ORDER = [
  INIT_PHASES.STORE_READY,
  INIT_PHASES.PROJECT_FETCHING,
  INIT_PHASES.PROJECT_DOWNLOADING,
  INIT_PHASES.PROJECT_STORING,
  INIT_PHASES.SCENE_CREATED,
  INIT_PHASES.MODELS_LOADED,
  INIT_PHASES.ANIMATION_READY,
  INIT_PHASES.COMPARATIVE_READY,
  INIT_PHASES.UI_READY,
  INIT_PHASES.COMPLETE
];

let currentPhase = null;
let currentProgress = 0;
let currentError = null;
const progressListeners = new Set();

/**
 * Initialise le systeme de phases
 * Note : ne clear pas les progressListeners car le Logo React
 * s'abonne avant que le Context n'appelle initPhaseSystem()
 * (les useEffect enfants s'executent avant les parents)
 */
export const initPhaseSystem = () => {
  currentPhase = null;
  currentProgress = 0;
  currentError = null;
};

/**
 * Retourne le pourcentage de progression (0-100)
 */
export const getProgress = () => currentProgress;

/**
 * Retourne le label de la phase actuelle
 */
export const getPhaseLabel = () => {
  if (!currentPhase) return getPhaseLabels()[INIT_PHASES.STORE_READY];
  return getPhaseLabels()[currentPhase] || currentPhase;
};

/**
 * Retourne la phase actuelle
 */
export const getCurrentPhase = () => currentPhase;

/**
 * Retourne true si l'initialisation est terminee
 */
export const isInitComplete = () => currentPhase === INIT_PHASES.COMPLETE;

/**
 * Retourne true si l'initialisation est en erreur
 */
export const isInitError = () => currentPhase === INIT_PHASES.ERROR;

/**
 * Retourne le message d'erreur courant
 */
export const getError = () => currentError;

/**
 * Met l'orchestrateur en etat d'erreur avec un message
 * @param {string} message - Message d'erreur a afficher
 */
export const setError = (message) => {
  console.error(`[InitPhase] ERROR: ${message}`);
  currentError = message;
  currentPhase = INIT_PHASES.ERROR;
  Store.dispatch({ type: 'LOADING_ACTION', payload: message });
  notifyProgressListeners();
};

/**
 * Calcule le pourcentage de progression pour une phase donnee
 */
const getPhaseProgress = (phase) => {
  const index = PHASE_ORDER.indexOf(phase);
  if (index === -1) return 0;
  return Math.round((index / (PHASE_ORDER.length - 1)) * 100);
};

/**
 * Permet de mettre a jour la progression intermediaire (ex: download %)
 * @param {number} percent - Pourcentage entre 0 et 100 pour la sous-etape
 */
export const setSubProgress = (percent) => {
  const phaseIndex = PHASE_ORDER.indexOf(currentPhase);
  if (phaseIndex === -1) return;
  
  const phaseStart = getPhaseProgress(currentPhase);
  const nextPhase = PHASE_ORDER[phaseIndex + 1];
  const phaseEnd = nextPhase ? getPhaseProgress(nextPhase) : 100;
  
  currentProgress = Math.round(phaseStart + (phaseEnd - phaseStart) * (percent / 100));
  notifyProgressListeners();
};

/**
 * Notifie tous les listeners de progression
 */
const notifyProgressListeners = () => {
  progressListeners.forEach(callback => {
    try {
      callback(currentProgress, currentPhase, getPhaseLabel());
    } catch (err) {
      console.error('[InitPhase] Error in progress listener:', err);
    }
  });
};

/**
 * S'abonne aux changements de progression
 * @param {Function} callback - (progress, phase, label) => void
 * @returns {Function} unsubscribe
 */
export const onProgress = (callback) => {
  progressListeners.add(callback);
  // Envoyer l'etat actuel immediatement
  callback(currentProgress, currentPhase, getPhaseLabel());
  return () => progressListeners.delete(callback);
};

/**
 * Definit la phase actuelle et notifie les listeners
 * @param {string} phase - Nom de la phase
 */
export const setPhase = (phase) => {
  if (currentPhase === phase) return;
  
  console.log(`[InitPhase] ${currentPhase || 'null'} -> ${phase}`);
  currentPhase = phase;
  currentProgress = getPhaseProgress(phase);
  
  // Dispatcher le message de loading dans le store Redux
  Store.dispatch({ type: 'LOADING_ACTION', payload: getPhaseLabels()[phase] || phase });
  
  // Notifier les listeners de progression
  notifyProgressListeners();
};

/**
 * Execute une fonction apres le prochain tick de render sans setTimeout
 * Utilise requestAnimationFrame pour etre plus efficace
 * @returns {Promise}
 */
export const nextRenderTick = () => {
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
};

/**
 * Attend un delai specifique en ms
 * A utiliser quand on a besoin d'un delai intentionnel (pas un next tick)
 * @param {number} ms - Delai en millisecondes
 * @returns {Promise}
 */
export const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export { currentPhase };
